import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './TabNavigator.scss';

/**
 * Represents a navigation tab in the Peacock Navigator.
 * Each tab has a unique name identifier and a display name shown in the breadcrumb.
 */
export interface Tab {
  /** Unique identifier for the tab (e.g., 'main', 'FieldsManager') */
  name: string;
  /** Human-readable name displayed in the breadcrumb (e.g., 'Edit Widget', 'Manage Fields') */
  displayName: string;
}

/**
 * Props for the TabNavigator component.
 */
interface TabNavigatorProps {
  /** The currently active tab */
  currentTab: Tab;
  /** Callback invoked when navigating to a different tab */
  onNavigate: (tab: Tab, poppedTabs: Tab[]) => void;
  /** Initial tab to start the navigation history */
  initialTab: Tab;
  /** Optional maximum number of breadcrumb items to display (shows the most recent items) */
  maxCrumbs?: number;
}

/**
 * Reusable breadcrumb navigation component with history management.
 *
 * This component maintains a navigation history as an array stack, allowing users to:
 * - Navigate forward by pushing new tabs to the history
 * - Navigate backward by removing tabs from the history
 * - Click any breadcrumb in the chain to jump back to that tab
 *
 * The component is designed to be consumed by another view that will manage `currentTab`.
 *
 *
 * @example
 * ```tsx
 * <TabNavigator
 *   currentTab={currentTab}
 *   onNavigate={(tab, poppedTabs) => setCurrentTab(tab)}
 *   initialTab={{ name: 'main', displayName: 'Edit Widget' }}
 *   maxCrumbs={2}
 * />
 * ```
 */
export const TabNavigator: React.FC<TabNavigatorProps> = ({
  currentTab,
  onNavigate,
  initialTab,
  maxCrumbs,
}) => {
  // History array maintains the navigation stack (push = forward, slice = backward)
  const [historyArray, setHistoryArray] = useState<Tab[]>([initialTab]);

  /**
   * Syncs the history array when currentTab changes externally.
   * If the current tab changes to a new view, it's automatically added to the history.
   */
  useEffect(() => {
    const lastTab = historyArray[historyArray.length - 1];

    // If currentTab changed to something not in history, add it
    if (lastTab.name !== currentTab.name) {
      goToTab(currentTab);
    }
  }, [currentTab]);

  /**
   * Navigates to a specific tab by adding it to or adjusting the history array.
   * @param tab - The tab to navigate to
   */
  const goToTab = (tab: Tab) => {
    // Check if tab already exists in history to avoid duplicates
    const existingIndex = historyArray.findIndex((t) => t.name === tab.name);

    if (existingIndex === -1) {
      // Tab doesn't exist, add it (array push)
      setHistoryArray((prevHistoryArray) => [...prevHistoryArray, tab]);
    } else if (existingIndex < historyArray.length - 1) {
      // Tab exists but not at the end, navigate back to it (array remove)
      setHistoryArray(historyArray.slice(0, existingIndex + 1));
    }
    // If it's already the current tab (last in array), do nothing
  };

  /**
   * Handles clicking a breadcrumb to navigate back to a specific index in history.
   * Removes all tabs after the clicked index from the history array.
   *
   * @param index - The position in the history array to navigate back to
   */
  const backToTab = useCallback(
    (index: number) => {
      const newHistory = historyArray.slice(0, index + 1);
      const poppedTabs = historyArray.slice(index + 1);
      setHistoryArray(newHistory);
      onNavigate(newHistory[newHistory.length - 1], poppedTabs);
    },
    [historyArray, onNavigate]
  );

  const backArrow = <span className="back-arrow">← </span>;

  // Compute breadcrumb content once per render using memoization
  const breadcrumbContent = useMemo(() => {
    const hasHistory = historyArray.length > 1;
    const currentTab = historyArray[historyArray.length - 1];

    // Simplified maxCrumbs === 1 handling
    if (maxCrumbs === 1) {
      return (
        <>
          {hasHistory && (
            <button
              className="breadcrumb-back-button"
              onClick={() => backToTab(historyArray.length - 2)}
            >
              {backArrow}
            </button>
          )}
          <span className="breadcrumb-current">{currentTab.displayName}</span>
        </>
      );
    }

    // Normal case: show breadcrumb trail
    const displayHistory = maxCrumbs ? historyArray.slice(-maxCrumbs) : historyArray;
    const historyOffset = historyArray.length - displayHistory.length;
    const lastIndex = historyArray.length - 1;

    return displayHistory.map((tab, displayIndex) => {
      const actualIndex = historyOffset + displayIndex;
      const isCurrentTab = actualIndex === lastIndex;
      const isFirstDisplayed = displayIndex === 0;

      return (
        <>
          {!isFirstDisplayed && <span className="breadcrumb-separator">/</span>}
          {isCurrentTab ? (
            <span className="breadcrumb-current">{tab.displayName}</span>
          ) : (
            <button className="breadcrumb-back-button" onClick={() => backToTab(actualIndex)}>
              {isFirstDisplayed && backArrow}
              {tab.displayName}
            </button>
          )}
        </>
      );
    });
  }, [historyArray, maxCrumbs, backToTab]);

  return <div className="pck-navigator">{breadcrumbContent}</div>;
};

export default TabNavigator;
